import {Button, NumberInput} from "@mantine/core";
import { Link as IconLink, List as IconList } from "@phosphor-icons/react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import Icon from "../../../components/Icon/Icon.tsx";
import IconMap from "../../../components/Icon/IconMap.ts";
import Page from "../../../components/Page/Page.tsx";
import SearchSelect from "../../../components/SearchSelect/SearchSelect";
import api, {useSWCLocation, useSystems} from "../../../utils/api.ts";
import {negativeInputMode} from "../../../utils/Utils.ts";
import ViewList from "./ViewList.jsx";
import ViewMap from "./ViewMap.jsx";
import {Col, Row} from "../../../components/Flex/Flex";


const calcDistance = (tavern, location) => {
    let {x, y} = location;
    return Math.max(Math.abs(x-tavern.system.x), Math.abs(y-tavern.system.y));
}

function ViewTaverns () {

    const [networks, setNetworks] = useState({});
    const {location: initLocation} = useSWCLocation();
    const params = useParams();
    const navigate = useNavigate();

    const [filterNetwork, setFilterNetwork] = useState(params?.network ?? null);
    const [view, setView] = useState(params?.view ?? 'map');
    const [location, setLocation] = useState({
        id: initLocation?.system.attributes.uid ?? '',
        x: initLocation?.coordinates.galaxy.attributes.x ?? null,
        y: initLocation?.coordinates.galaxy.attributes.y ?? null,

    });
    const selectedNetwork = useMemo(() => {
        if(params.network) {
            return networks[params.network]?.owner.name;
        } else {
            return null;
        }
    }, [params.network, networks]);

    useEffect(() => {
        api.get('/bh/taverns').then(res => {
            let data = res.data;
            setNetworks(data);
        })
    }, []);

    const availableNetworks = useMemo(() => {
        return Object.values(networks).filter(n => n.taverns.length>0).map(n => ({...n.owner, value: n.owner.id, label: `${n.owner.name} (${n.taverns.length})`})).sort((a,b) => a.name.localeCompare(b.name));
    }, [networks]);

    const enrichedData = useMemo(() => {
        if(location.x !== null && location.y !== null) {
            let enrichedData = {...networks};
            for(let id in enrichedData) {
                enrichedData[id].taverns = enrichedData[id].taverns.map(t => ({...t, distance: calcDistance(t, location)}));
            }
            return enrichedData;
        } else {
            return networks;
        }
    }, [networks, location]);


    const filtered = useMemo(() => {
        if(!filterNetwork) {
            let temp = Object.values(enrichedData);

            return temp.reduce((tot, curr) => tot.concat(curr.taverns), []);
        }
        if(enrichedData[filterNetwork]) {
            return enrichedData[filterNetwork].taverns;
        } else {
            return [];
        }

    }, [filterNetwork, enrichedData, location]);

    const networkSelectData = useMemo(() => {
        return availableNetworks.map(n => ({value: n.id, label: n.label}));
    }, [availableNetworks]);

    const toggleElement = useMemo(() => (
        view === 'map' ?
            <Button leftSection={<IconList size={16}/>} onClick={() => setView('list')}>List</Button> :
            <Button leftSection={<Icon icon={IconMap.Galaxy} className="p-button-icon-left" />} onClick={() => setView('map')}>Map</Button>

    ), [view, setView]);

    const filterElement = useMemo(() => (
        <Row align="center" wrap={false}>
            <SearchSelect
                value={filterNetwork}
                onChange={v => setFilterNetwork(v)}
                data={networkSelectData}
                placeholder="Filter by network"
                clearable={true}
            />
            {filterNetwork ? <IconLink size={16} className="clickable" onClick={() => navigate('/bh/taverns/'+view+'/'+filterNetwork)} title="Get direct link"/> : null}
        </Row>
    ), [filterNetwork, networkSelectData, view]);

    const filterLocation = useMemo(() => {
        return <LocationSelector location={location} setLocation={setLocation} />;
    }, [location, setLocation]);


    return (
        <Page>
            <title>{selectedNetwork ? selectedNetwork+' Network' : "Tavern Networks"}</title>
            {view === 'map' ? <ViewMap filteredData={filtered} filterElement={filterElement} toggleElement={toggleElement} /> : null}
            {view === 'list' ? <ViewList filteredData={filtered} filterElement={filterElement} toggleElement={toggleElement} locationElement={filterLocation} hasLocation={location.x !== null && location.y !== null} /> : null}
        </Page>
    )
}

const LocationSelector = ({ location, setLocation }) => {
    const {systems} = useSystems();

    const systemSelectData = useMemo(() => {
        return systems.map(s => ({value: s.id, label: s.name}));
    }, [systems]);

    const onCoordChange = useCallback((type, val) => {
        setLocation(prev => {
            let x = type==='x' ? val : prev.x;
            let y = type==='y' ? val : prev.y;

            let s = systems.find(s => s.galX == x && s.galY == y);
            if(s) {
                return {id: s.id, x, y};
            } else {
                return {id: '', x, y};
            }
        })
    }, [systems]);

    const onSystemSelect = useCallback((v) => {
        let s = systems.find(s => s.id === v);
        if(s) {
            setLocation({id: v, x: s.galX, y: s.galY});
        }
    }, [systems]);
    return (
        <Row align="end" wrap={false}>
            <Col basis="160px" shrink={0}>
                <SearchSelect
                    label="Distance from"
                    id="system"
                    data={systemSelectData}
                    value={location?.id || null}
                    onChange={onSystemSelect}
                    searchable
                    placeholder="System"
                />
            </Col>
            <Col><NumberInput style={{width: 60}} value={location.x} min={-500} max={500} onChange={(v) => onCoordChange('x', v)} placeholder="X" inputMode={negativeInputMode} hideControls /></Col>
            <Col><NumberInput style={{width: 60}} value={location.y} min={-500} max={500} onChange={(v) => onCoordChange('y', v)} placeholder="Y" inputMode={negativeInputMode} hideControls /></Col>
        </Row>
    )
}


export default withRouteErrorBoundary(ViewTaverns);
